import { createContext, useEffect, useReducer, useState } from "react"
import { usePropertyQuery } from "./propertyQueries"
import { Property, createNewProperty, propertyHasChanges } from "./propertyModel"
import editPropertyReducer, { Action } from "./editPropertyReducer"

type EditPropertyContextData = {
  googleMapsApiKey: string
  customerId?: number
  selectedPropertyId?: number
  selectProperty: (propertyId: number | undefined, skipConfirmation: boolean) => void
  property?: Property
  propertyIsLoading: boolean
  editState: Property
  dispatch: (action: Action) => void
  hasChanges: boolean
  showDeletedView: boolean
}

export const createEmptyEditPropertyContextData = (): EditPropertyContextData => ({
  googleMapsApiKey: '',
  customerId: 0,
  selectProperty: () => {},
  propertyIsLoading: false,
  editState: createNewProperty(0),
  dispatch: () => {},
  hasChanges: false,
  showDeletedView: false
})

const EditPropertyContext = createContext<EditPropertyContextData>(createEmptyEditPropertyContextData())

export default EditPropertyContext

type Props = React.PropsWithChildren & {
  googleMapsApiKey: string
  customerId?: number
  initialPropertyId?: number
  onPropertyChange: (propertyId: number | undefined) => void
  showDeletedView: boolean
}

export const EditPropertyContextWrapper = ({customerId, googleMapsApiKey, initialPropertyId, onPropertyChange, showDeletedView, children}: Props) => {
  const [editState, dispatch] = useReducer(editPropertyReducer, createNewProperty(customerId ?? 0))
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | undefined>(initialPropertyId)
  const propertyQuery = usePropertyQuery(customerId, selectedPropertyId, showDeletedView)

  useEffect(() => {
    if (propertyQuery.data) {
      dispatch({ type: 'propertyLoaded', property: propertyQuery.data })
    }
  }, [propertyQuery.data])

  const hasChanges = propertyHasChanges(propertyQuery.data, editState)

  const handlePropertyChange = (propertyId: number | undefined, skipConfirmation: boolean) => {
    if (propertyId === selectedPropertyId) {
      return
    }

    if (hasChanges && !skipConfirmation) {
      if (!confirm("This property has been edited.\n\nDiscard your changes and select a different property?")) {
        return false;
      }
    }
    setSelectedPropertyId(propertyId)
    onPropertyChange(propertyId)
  }

  return <EditPropertyContext.Provider value={{
    googleMapsApiKey,
    customerId,
    selectedPropertyId,
    selectProperty: handlePropertyChange,
    property: propertyQuery.data,
    propertyIsLoading: propertyQuery.isLoading || propertyQuery.isFetching,
    editState,
    dispatch,
    hasChanges,
    showDeletedView
  }}>
    {children}
  </EditPropertyContext.Provider>
}
